import React, { useCallback, useEffect, useState } from 'react'
import { MdCheck } from 'react-icons/md'
import { toast } from 'react-toastify'
import PropTypes from 'prop-types'

import { request } from '../../../integration'
import Avatar from '../Avatar/Avatar'
import Flex from '../Flex/Flex'
import LoadingBounce from '../LoadingBounce/LoadingBounce'
import Text from '../Text/Text'
import Button from '../Button/Button'
import useDebounce from '../../../hooks/useDebounce'
import AssignMessage from './AssignMessage'
import styles from './assignStore.module.scss'

const AssignStore = ({ stdCode, callbackClose, onApply }) => {
  const [search, setSearch] = useState('')
  const [walletCodes, setWalletCodes] = useState([])
  const [items, setItems] = useState([])
  const [showMessage, setShowMessage] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loadingApply, setLoadingApply] = useState(false)

  const loadWallets = useCallback(async () => {
    setLoading(true)
    try {
      const params = {}

      if (search) {
        params.search = search
      }
      const { data } = await request.get(
        `/v1/superviseds/${stdCode}/available-stores`,
        { params }
      )

      const assigned = []
      data.forEach((item) => {
        if (item.alreadyAssignedToSupervised) {
          assigned.push(item.walletCode)
        }
      })

      setItems(data)
      setWalletCodes(assigned)
    } catch (err) {
      toast.error('Erro ao buscar lojas')
    } finally {
      setLoading(false)
    }
  }, [search, stdCode])

  useEffect(() => {
    loadWallets()
  }, [stdCode])

  useDebounce(
    () => {
      loadWallets()
    },
    [search],
    400
  )

  const checkItem = useCallback(
    (itemId) => {
      if (walletCodes.includes(itemId)) {
        const newlist = walletCodes.filter((i) => i !== itemId)
        setWalletCodes(newlist)
      } else {
        setWalletCodes([...walletCodes, itemId])
      }
    },
    [setWalletCodes, walletCodes]
  )

  const handleMessage = () => {
    setShowMessage(true)
  }

  const handleCancel = () => {
    setShowMessage(false)
    setSearch('')
    setWalletCodes([])
    callbackClose()
  }

  const handleApply = async () => {
    setLoadingApply(true)
    try {
      await request.put(`/v1/superviseds/${stdCode}/wallets`, {
        walletCodes
      })

      onApply()
    } catch (error) {
      toast.error('Não foi possível concluir está operacão')
    } finally {
      toast.success('Operação concluída com sucesso')
    }
  }

  return !loadingApply ? (
    <Flex column alignItems="center">
      <Text color="gray" variation="dark" size="lg" bold>
        {showMessage ? 'Atenção' : 'Adicionar clientes'}
      </Text>

      {!showMessage ? (
        <>
          <input
            placeholder="Pesquisar"
            value={search}
            className={styles.input}
            onChange={(e) => setSearch(e.target.value)}
          />

          <div className={styles.list}>
            {loading && (
              <Flex alignItems="center" justifyContent="center">
                <LoadingBounce />
              </Flex>
            )}
            {!loading && items.length === 0 && (
              <div className={styles.empty}>
                <Text color="gray" variation="light" size="xxs">
                  Não há clientes disponíveis
                </Text>
              </div>
            )}
            {items.map((item) => (
              <div key={item.id} className={styles.item}>
                <Flex alignItems="center">
                  <Avatar title={item.nameFantasy} size="small" />
                  <Flex column margin="0px 0px 0px 8px">
                    <Text color="gray" variation="dark" bold size="xxs">
                      {item.nameFantasy}
                    </Text>
                    <Text size="xxxs" color="gray">
                      {item.groupName ?? 'N/A'} - {item.tabNumber}
                    </Text>
                  </Flex>
                </Flex>

                <label className={styles.checkbox}>
                  <span className={styles.checkbox__input}>
                    <input
                      type="checkbox"
                      name="checkbox"
                      onChange={() => checkItem(item.walletCode)}
                      checked={walletCodes.includes(item.walletCode)}
                    />
                    <span className={styles.checkbox__control}>
                      <MdCheck color="#fff" />
                    </span>
                  </span>
                </label>
              </div>
            ))}
          </div>
          <Flex
            alignItems="center"
            justifyContent="center"
            margin="15px 0px 0px 0px"
          >
            <Button type="secondary" onClick={handleMessage}>
              <Text size="xxs" color="white">
                Aplicar
              </Text>
            </Button>
          </Flex>
        </>
      ) : (
        <AssignMessage
          amount={walletCodes.length}
          handleApply={handleApply}
          handleCancel={handleCancel}
        />
      )}
    </Flex>
  ) : (
    <div className={styles.loading}>
      <LoadingBounce />
    </div>
  )
}

AssignStore.propTypes = {
  stdCode: PropTypes.string.isRequired,
  callbackClose: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired
}

export default AssignStore
